import { Link } from "react-router-dom";
import { Check, FileText, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";

const LICENSE_TIERS = [
  {
    id: "basic",
    name: "Basic Lease",
    price: 29.99,
    description: "Perfect for independent artists starting out",
    features: [
      "MP3 file download (320kbps)",
      "2,500 streaming plays",
      "Non-exclusive license",
      "Producer keeps 20% publishing royalties",
      "Credit required in title",
      "1 music video allowed",
      "Valid for 1 year",
    ],
    notIncluded: ["WAV/Stems", "Radio broadcasting", "Commercial use"],
  },
  {
    id: "premium",
    name: "Premium Lease",
    price: 59.99,
    description: "Best value for serious artists",
    popular: true,
    features: [
      "MP3 + WAV file download",
      "10,000 streaming plays",
      "Non-exclusive license",
      "Producer keeps 20% publishing royalties",
      "Credit required in title",
      "Unlimited music videos",
      "Radio broadcasting rights",
      "Valid for 2 years",
    ],
    notIncluded: ["Full ownership", "Unlimited streams"],
  },
  {
    id: "exclusive",
    name: "Exclusive Rights",
    price: 500,
    description: "Full ownership and control",
    features: [
      "Full ownership transfer",
      "All file formats included",
      "Unlimited streaming plays",
      "Beat removed from store",
      "No credit required",
      "Full commercial rights",
      "Lifetime license",
    ],
    notIncluded: [],
  },
];

export default function Licensing() {
  return (
    <div className="pt-24 pb-12 px-6 lg:px-8" data-testid="licensing-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="font-heading text-4xl lg:text-5xl font-black">
            Licensing Options
          </h1>
          <p className="text-muted-foreground mt-4 text-lg max-w-2xl mx-auto">
            Choose the perfect license for your project. All licenses include 
            instant delivery and lifetime customer support.
          </p>
        </motion.div>

        {/* License Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {LICENSE_TIERS.map((tier, index) => (
            <motion.div
              key={tier.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`relative glass rounded-3xl p-8 ${
                tier.popular ? "border-2 border-primary neon-glow" : "border border-white/10"
              }`}
              data-testid={`license-tier-${tier.id}`}
            >
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-primary text-white text-sm font-bold px-4 py-1.5 rounded-full">
                    MOST POPULAR
                  </span>
                </div>
              )}

              <div className="mb-6">
                <h3 className="font-heading font-bold text-2xl">{tier.name}</h3>
                <p className="text-muted-foreground text-sm mt-1">
                  {tier.description}
                </p>
              </div>

              <div className="mb-8">
                <span className="font-heading text-5xl font-black text-primary">
                  ${tier.price}
                </span>
                <span className="text-muted-foreground ml-2">per beat</span>
              </div>

              <div className="space-y-3 mb-8">
                {tier.features.map((feature, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
                {tier.notIncluded.map((item, i) => (
                  <div key={i} className="flex items-start gap-3 opacity-50">
                    <span className="w-5 h-5 flex items-center justify-center text-destructive">×</span>
                    <span className="text-sm line-through">{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/store">
                <Button
                  className={`w-full rounded-full h-12 font-bold ${
                    tier.popular
                      ? "bg-primary hover:bg-primary/90 neon-glow"
                      : "bg-white/10 hover:bg-white/20"
                  }`}
                  data-testid={`license-select-${tier.id}`}
                >
                  Browse Beats
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Lease Agreement Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="glass rounded-3xl p-8 lg:p-12"
          data-testid="lease-agreement-section"
        >
          <div className="flex items-start gap-6 mb-8">
            <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center flex-shrink-0">
              <FileText className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h2 className="font-heading text-2xl lg:text-3xl font-bold mb-2">
                Lease Agreement
              </h2>
              <p className="text-muted-foreground">
                Please read our licensing terms carefully before making a purchase
              </p>
            </div>
          </div>

          <div className="prose prose-invert max-w-none">
            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <h4 className="font-heading font-bold text-lg mb-4 text-foreground">
                  General Terms
                </h4>
                <ul className="space-y-3 text-muted-foreground text-sm">
                  <li>• All beats are sold as-is with instant delivery upon payment confirmation.</li>
                  <li>• The producer retains ownership of the beat until an Exclusive license is purchased.</li>
                  <li>• Non-exclusive licenses allow the beat to be sold to multiple artists.</li>
                  <li>• License terms begin from the date of purchase.</li>
                  <li>• Streaming counts are based on major platforms (Spotify, Apple Music, YouTube, etc.).</li>
                </ul>
              </div>

              <div>
                <h4 className="font-heading font-bold text-lg mb-4 text-foreground">
                  Usage Rights
                </h4>
                <ul className="space-y-3 text-muted-foreground text-sm">
                  <li>• Credit must be given in the format: "Prod. by [Producer Name]"</li>
                  <li>• Beats cannot be resold, redistributed, or given away.</li>
                  <li>• Exclusive licenses transfer full ownership and remove the beat from sale.</li>
                  <li>• No refunds are offered for digital products after delivery.</li>
                  <li>• Contact us for custom licensing arrangements or bulk deals.</li>
                </ul>
              </div>
            </div>

            <div className="mt-8 p-6 bg-primary/10 rounded-2xl border border-primary/20">
              <h4 className="font-heading font-bold text-lg mb-2 text-foreground">
                Need a Custom License?
              </h4>
              <p className="text-muted-foreground text-sm mb-4">
                Have specific requirements? We offer custom licensing for major labels, 
                sync placements, and commercial projects. Get in touch to discuss your needs.
              </p>
              <Link to="/contact">
                <Button className="rounded-full bg-primary hover:bg-primary/90">
                  Contact Us
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>

        {/* FAQ Preview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-16"
        >
          <h3 className="font-heading text-2xl font-bold mb-8 text-center">
            Frequently Asked Questions
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                q: "Can I use the beat commercially?",
                a: "Yes! All licenses include commercial rights. The difference is in the scope of usage (streaming limits, distribution channels, etc.)."
              },
              {
                q: "What happens if I exceed my streaming limit?",
                a: "You'll need to upgrade to a higher tier license. Contact us and we'll help you transition smoothly without penalties."
              },
              {
                q: "Do you offer refunds?",
                a: "Due to the digital nature of our products, we don't offer refunds. Make sure to preview beats thoroughly before purchasing."
              },
              {
                q: "Can I get a custom beat made?",
                a: "Absolutely! Use our contact form to discuss custom beat production. Prices vary based on complexity and timeline."
              }
            ].map((faq, i) => (
              <div key={i} className="glass rounded-2xl p-6">
                <h4 className="font-heading font-bold mb-2">{faq.q}</h4>
                <p className="text-muted-foreground text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
